import createClient, { type Middleware } from "openapi-fetch"
import { apiV1BaseUrl } from "@/shared/config/env"
import {
  clearTokens,
  getAccessToken,
  setAccessToken,
} from "@/shared/auth/tokenStore"
import type { AccessTokenResponse, Problem, paths } from "./types"

/** Ошибка API с HTTP-статусом и (если есть) телом `application/problem+json`. */
export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem | undefined
  readonly url: string

  constructor(status: number, url: string, problem?: Problem, fallbackMessage?: string) {
    super(problem?.detail || problem?.title || fallbackMessage || `HTTP ${status}`)
    this.name = "ApiError"
    this.status = status
    this.url = url
    this.problem = problem
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError

/** Человекочитаемое сообщение из любой ошибки (ApiError / Error / неизвестное). */
export const getApiErrorMessage = (error: unknown, fallback = "Произошла ошибка"): string => {
  if (isApiError(error)) {
    const fieldErrors = error.problem?.errors
    if (fieldErrors && fieldErrors.length > 0) {
      return fieldErrors.map((item) => `${item.field}: ${item.message}`).join("; ")
    }
    return error.message || fallback
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/** Auth operations deliberately bypass the 401 middleware to prevent recursion. */
export const authApi = createClient<paths>({
  baseUrl: apiV1BaseUrl,
  credentials: "include",
  fetch: (...args) => fetch(...args),
})

let refreshInFlight: Promise<string> | null = null
let accessGeneration = 0
let failedRefreshGeneration = -1

/** Records a server-issued access token and re-enables cookie refresh. */
export const acceptAccessToken = (tokens: AccessTokenResponse) => {
  accessGeneration += 1
  failedRefreshGeneration = -1
  setAccessToken(tokens.access_token)
  return tokens.access_token
}

export const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight
  if (failedRefreshGeneration === accessGeneration) {
    return Promise.reject(new ApiError(401, "/auth/refresh", undefined, "Refresh session unavailable"))
  }

  const attempt = (async () => {
    const tokens = await unwrap<AccessTokenResponse>(authApi.POST("/auth/refresh"))
    return acceptAccessToken(tokens)
  })().catch((error) => {
    failedRefreshGeneration = accessGeneration
    clearTokens()
    throw error
  })

  refreshInFlight = attempt
  void attempt.finally(() => {
    if (refreshInFlight === attempt) refreshInFlight = null
  }).catch(() => undefined)
  return attempt
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = getAccessToken()
    if (token && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${token}`)
    }
    return request
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response
    try {
      const requestToken = request.headers.get("Authorization")
      const currentToken = getAccessToken()
      const token =
        currentToken && requestToken !== `Bearer ${currentToken}`
          ? currentToken
          : await refreshAccessToken()
      const retry = new Request(request)
      retry.headers.set("Authorization", `Bearer ${token}`)
      return fetch(retry)
    } catch {
      return response
    }
  },
}

/** Типизированный клиент openapi-fetch. Все массивы в query сериализуются как `a,b` (style=form, explode=false). */
export const api = createClient<paths>({
  baseUrl: apiV1BaseUrl,
  credentials: "same-origin",
  fetch: (...args) => fetch(...args),
  querySerializer: {
    array: { style: "form", explode: false },
  },
})

api.use(authMiddleware)

type FetchResult<T> = {
  data?: T
  error?: unknown
  response: Response
}

/**
 * Разворачивает результат openapi-fetch: возвращает `data` или кидает `ApiError`.
 * Для ответов без тела (204) возвращает `undefined as T`.
 */
export const unwrap = async <T>(promise: Promise<FetchResult<T>>): Promise<T> => {
  const { data, error, response } = await promise
  if (!response.ok) {
    const problem = error && typeof error === "object" && "title" in (error as object) ? (error as Problem) : undefined
    throw new ApiError(response.status, response.url, problem, response.statusText)
  }
  if (error !== undefined && data === undefined) {
    throw new ApiError(response.status, response.url, undefined, "Некорректный ответ API")
  }
  return data as T
}

/** URL файла экспорта (скачивание через `<a download>`). */
export const exportUrl = (type: string, id: string, format: string) =>
  `${apiV1BaseUrl}/${encodeURIComponent(type)}/${encodeURIComponent(id)}/export?format=${encodeURIComponent(format)}`
